import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Save, Plus, Trash2, Globe, Phone, Mail, MapPin, Clock, Facebook, Youtube, Instagram } from 'lucide-react'
import { useSite } from '../../context/SiteContext'
import { updateDocument } from '../../services/firebase'
import { Button, Input, Textarea, Card, Badge } from '../../components/common'

const SiteSettings = () => {
  const { settings } = useSite()
  const [formData, setFormData] = useState(settings)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setFormData(settings)
  }, [settings])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)

    try {
      await updateDocument('settings', 'site', formData)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (error) {
      console.error('Error saving settings:', error)
    }

    setSaving(false)
  }

  const addServiceTime = () => {
    setFormData({
      ...formData,
      serviceTimes: [
        ...formData.serviceTimes,
        { day: '', name: '', time: '' },
      ],
    })
  }

  const removeServiceTime = (index) => {
    setFormData({
      ...formData,
      serviceTimes: formData.serviceTimes.filter((_, i) => i !== index),
    })
  }

  const updateServiceTime = (index, field, value) => {
    const updated = [...formData.serviceTimes]
    updated[index] = { ...updated[index], [field]: value }
    setFormData({ ...formData, serviceTimes: updated })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-dark-100">Site Settings</h1>
          <p className="text-gray-500">Configure church information and website settings</p>
        </div>
        <Button
          variant="primary"
          icon={Save}
          onClick={handleSubmit}
          loading={saving}
        >
          {saved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Church Information */}
        <Card animate={false} className="p-6">
          <h2 className="text-lg font-semibold text-dark-100 mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary-600" />
            Church Information
          </h2>

          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Church Name"
              value={formData.churchName}
              onChange={(e) => setFormData({ ...formData, churchName: e.target.value })}
            />
            <Input
              label="Tagline"
              value={formData.tagline}
              onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
            />
          </div>
        </Card>

        {/* Contact Information */}
        <Card animate={false} className="p-6">
          <h2 className="text-lg font-semibold text-dark-100 mb-4 flex items-center gap-2">
            <Phone className="w-5 h-5 text-primary-600" />
            Contact Information
          </h2>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <Input
              label="Phone"
              icon={Phone}
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
            <Input
              label="Email"
              icon={Mail}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Address
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Street Address"
              value={formData.address?.street}
              onChange={(e) => setFormData({
                ...formData,
                address: { ...formData.address, street: e.target.value },
              })}
            />
            <Input
              label="City"
              value={formData.address?.city}
              onChange={(e) => setFormData({
                ...formData,
                address: { ...formData.address, city: e.target.value },
              })}
            />
            <Input
              label="State"
              value={formData.address?.state}
              onChange={(e) => setFormData({
                ...formData,
                address: { ...formData.address, state: e.target.value },
              })}
            />
            <Input
              label="ZIP Code"
              value={formData.address?.zip}
              onChange={(e) => setFormData({
                ...formData,
                address: { ...formData.address, zip: e.target.value },
              })}
            />
          </div>
        </Card>

        {/* Service Times */}
        <Card animate={false} className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-dark-100 flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary-600" />
              Service Times
            </h2>
            <Button
              variant="outline"
              size="sm"
              icon={Plus}
              onClick={addServiceTime}
              type="button"
            >
              Add Service
            </Button>
          </div>

          <div className="space-y-4">
            {formData.serviceTimes?.map((service, index) => (
              <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-1 grid grid-cols-3 gap-4">
                  <Input
                    placeholder="Day (e.g., Sunday)"
                    value={service.day}
                    onChange={(e) => updateServiceTime(index, 'day', e.target.value)}
                  />
                  <Input
                    placeholder="Service Name"
                    value={service.name}
                    onChange={(e) => updateServiceTime(index, 'name', e.target.value)}
                  />
                  <Input
                    placeholder="Time (e.g., 10:30 AM)"
                    value={service.time}
                    onChange={(e) => updateServiceTime(index, 'time', e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeServiceTime(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>

          <Input
            label="Office Hours"
            className="mt-4"
            value={formData.officeHours}
            onChange={(e) => setFormData({ ...formData, officeHours: e.target.value })}
            placeholder="Monday - Friday: 9:00 AM - 5:00 PM"
          />
        </Card>

        {/* Social Media */}
        <Card animate={false} className="p-6">
          <h2 className="text-lg font-semibold text-dark-100 mb-4">Social Media</h2>

          <div className="grid md:grid-cols-3 gap-4">
            <Input
              label="Facebook"
              icon={Facebook}
              value={formData.socialMedia?.facebook}
              onChange={(e) => setFormData({
                ...formData,
                socialMedia: { ...formData.socialMedia, facebook: e.target.value },
              })}
              placeholder="https://facebook.com/..."
            />
            <Input
              label="YouTube"
              icon={Youtube}
              value={formData.socialMedia?.youtube}
              onChange={(e) => setFormData({
                ...formData,
                socialMedia: { ...formData.socialMedia, youtube: e.target.value },
              })}
              placeholder="https://youtube.com/..."
            />
            <Input
              label="Instagram"
              icon={Instagram}
              value={formData.socialMedia?.instagram}
              onChange={(e) => setFormData({
                ...formData,
                socialMedia: { ...formData.socialMedia, instagram: e.target.value },
              })}
              placeholder="https://instagram.com/..."
            />
          </div>
        </Card>

        {/* SEO Settings */}
        <Card animate={false} className="p-6">
          <h2 className="text-lg font-semibold text-dark-100 mb-4">SEO Settings</h2>

          <div className="space-y-4">
            <Textarea
              label="Meta Description"
              rows={2}
              value={formData.metaDescription}
              onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
              placeholder="Brief description for search engines..."
              helperText="Recommended: 150-160 characters"
            />
            <Input
              label="Meta Keywords"
              value={formData.metaKeywords}
              onChange={(e) => setFormData({ ...formData, metaKeywords: e.target.value })}
              placeholder="church, Clarksville, worship, community"
              helperText="Comma-separated keywords"
            />
            <Textarea
              label="Footer Text"
              rows={2}
              value={formData.footerText}
              onChange={(e) => setFormData({ ...formData, footerText: e.target.value })}
            />
          </div>
        </Card>

        {/* Save Button (Mobile) */}
        <div className="lg:hidden">
          <Button
            variant="primary"
            icon={Save}
            type="submit"
            fullWidth
            loading={saving}
          >
            {saved ? 'Saved!' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default SiteSettings
